import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  FaRocket, FaChartLine, FaCode, FaDatabase, FaAward, FaBolt, FaArrowRight,
} from 'react-icons/fa';
import digitalInnovationImg from '../../assets/digital_innovation.png';
import aestheticUxImg       from '../../assets/aesthetic_ux.png';
import analyticsDataImg     from '../../assets/analytics_data.png';

/* ─────────────────────────────────────────────────────────────────
   Animated counter
───────────────────────────────────────────────────────────────── */
const Counter = ({ target, suffix = '' }) => {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let v = 0;
    const step = Math.ceil(target / 60);
    const id = setInterval(() => {
      v = Math.min(v + step, target);
      setN(v);
      if (v >= target) clearInterval(id);
    }, 18);
    return () => clearInterval(id);
  }, [inView, target]);
  return <span ref={ref}>{n.toLocaleString()}{suffix}</span>;
};

/* ─────────────────────────────────────────────────────────────────
   Motion config — Apple cubic-bezier
───────────────────────────────────────────────────────────────── */
const E = [0.16, 1, 0.3, 1];

const col = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.78, ease: E } },
};
const imgAnim = {
  hidden: { opacity: 0, scale: 0.96 },
  show:   { opacity: 1, scale: 1,   transition: { duration: 0.95, ease: E } },
};

/* ─────────────────────────────────────────────────────────────────
   Apple-style image tile
───────────────────────────────────────────────────────────────── */
const ImageTile = ({ src, alt, shadow = 'dark', className = '' }) => (
  <motion.div
    variants={imgAnim}
    className={`relative overflow-hidden rounded-[28px] w-full ${className}`}
    style={{
      boxShadow: shadow === 'dark'
        ? '0 48px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)'
        : '0 32px 64px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.05)',
    }}
  >
    <img src={src} alt={alt}
      className="w-full h-full object-cover"
      loading="lazy" decoding="async"
    />
  </motion.div>
);

/* ─────────────────────────────────────────────────────────────────
   Apple-style horizontal stats bar
───────────────────────────────────────────────────────────────── */
const StatsBar = ({ stats, theme = 'dark' }) => {
  const border = theme === 'dark' ? 'border-white/[0.08]' : 'border-[#1D1D1F]/[0.08]';
  const numCls = theme === 'dark' ? 'text-white'     : 'text-[#1D1D1F]';
  const lblCls = theme === 'dark' ? 'text-white/40'  : 'text-[#6E6E73]';

  return (
    <motion.div
      variants={fadeUp}
      className={`grid grid-cols-${stats.length} border-t ${border} mt-14 sm:mt-16`}
    >
      {stats.map(({ value, suffix, label }, i) => (
        <div key={i}
          className={`py-8 sm:py-10 text-center ${i < stats.length - 1 ? `border-r ${border}` : ''}`}
        >
          <p className={`text-[36px] sm:text-[44px] font-bold tabular-nums leading-none tracking-tight ${numCls}`}>
            <Counter target={value} suffix={suffix} />
          </p>
          <p className={`text-[11px] uppercase tracking-[0.22em] font-semibold mt-2 ${lblCls}`}>{label}</p>
        </div>
      ))}
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   Gradient text helper
───────────────────────────────────────────────────────────────── */
const GradientText = ({ children, from, via, to, className = '' }) => (
  <span
    className={className}
    style={{
      background: `linear-gradient(90deg, ${from}${via ? `, ${via}` : ''}, ${to})`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    }}
  >
    {children}
  </span>
);

/* ═════════════════════════════════════════════════════════════════
   EDITORIAL SECTION
═════════════════════════════════════════════════════════════════ */
const EditorialSection = () => {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkScreen = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  // Enable html scroll snapping dynamically when desktop view is active
  useEffect(() => {
    if (isDesktop) {
      document.documentElement.classList.add('snap-y', 'snap-proximity', 'scroll-smooth');
    } else {
      document.documentElement.classList.remove('snap-y', 'snap-proximity', 'scroll-smooth');
    }
    return () => {
      document.documentElement.classList.remove('snap-y', 'snap-proximity', 'scroll-smooth');
    };
  }, [isDesktop]);

  const ref0 = useRef(null);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);

  const { scrollYProgress: progress0 } = useScroll({ target: ref0, offset: ["start end", "end start"] });
  const { scrollYProgress: progress1 } = useScroll({ target: ref1, offset: ["start end", "end start"] });
  const { scrollYProgress: progress2 } = useScroll({ target: ref2, offset: ["start end", "end start"] });
  const { scrollYProgress: progress3 } = useScroll({ target: ref3, offset: ["start end", "end start"] });

  // Parallax translation transforms (smoother, subtle motion)
  const textY0 = useTransform(progress0, [0, 1], [40, -40]);
  const imgY0 = useTransform(progress0, [0, 1], [-30, 30]);

  const textY1 = useTransform(progress1, [0, 1], [40, -40]);
  const imgY1 = useTransform(progress1, [0, 1], [-30, 30]);

  const textY2 = useTransform(progress2, [0, 1], [40, -40]);
  const imgY2 = useTransform(progress2, [0, 1], [-30, 30]);

  const textY3 = useTransform(progress3, [0, 1], [40, -40]);

  if (!isDesktop) {
    return (
      <div className="w-full">

        {/* ═══════════════════════════════════════════════════════════
            PANEL 1 — Transforming Businesses
            Dark (#1D1D1F) · Text LEFT · Image tile RIGHT
        ═══════════════════════════════════════════════════════════ */}
        <section className="bg-[#1D1D1F] overflow-hidden">
          <motion.div
            variants={col} initial="hidden"
            whileInView="show" viewport={{ once: true, margin: '-80px' }}
            className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 pt-20 sm:pt-28 lg:pt-32"
          >
            {/* Eyebrow */}
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-12 sm:mb-16">
              <span className="text-[10px] font-mono tracking-[0.2em] text-white/50">01</span>
              <div className="h-px w-8 bg-white/25" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-white/60 font-semibold">Digital Transformation</span>
            </motion.div>

            {/* Main grid */}
            <div className="grid lg:grid-cols-[55%,1fr] gap-10 lg:gap-16 items-center">

              {/* Text */}
              <div>
                <motion.h2 variants={fadeUp}
                  className="text-[52px] sm:text-[68px] lg:text-[84px] font-extralight text-white/60 leading-[1.04] tracking-tight">
                  Transforming
                </motion.h2>
                <motion.h2 variants={fadeUp}
                  className="text-[52px] sm:text-[68px] lg:text-[84px] font-black text-white leading-[1.04] tracking-tight -mt-1 sm:-mt-2">
                  Businesses.
                </motion.h2>
                <motion.p variants={fadeUp}
                  className="mt-4 text-lg sm:text-xl font-light leading-none">
                  <GradientText from="#FF7332" via="#FF1E90" to="#7000FF">
                    Through Innovation
                  </GradientText>
                </motion.p>

                <motion.p variants={fadeUp}
                  className="mt-8 text-[15px] sm:text-base text-[#86868B] leading-[1.75] max-w-[44ch] font-light">
                  At Cyborg Sapient, we operate at the intersection of technology and creative
                  performance — helping scaling businesses leverage AI automation, custom
                  engineering, and data-driven campaigns that compound over time.
                </motion.p>

                <motion.div variants={fadeUp} className="mt-10">
                  <Link to="/contact"
                    className="inline-flex items-center gap-2.5 text-[15px] font-medium text-white/70 hover:text-white transition-colors duration-200 group">
                    Start a project
                    <FaArrowRight size={11}
                      className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all duration-200" />
                  </Link>
                </motion.div>
              </div>

              {/* Image tile */}
              <ImageTile
                src={digitalInnovationImg}
                alt="Team driving digital transformation"
                shadow="dark"
                className="aspect-[3/4] lg:aspect-auto lg:h-[480px]"
              />
            </div>

            {/* Apple-style stats bar */}
            <StatsBar
              theme="dark"
              stats={[
                { value: 500, suffix: '+', label: 'Projects Delivered' },
                { value: 98,  suffix: '%', label: 'Client Retention'   },
                { value: 24,  suffix: 'h', label: 'Response SLA'       },
              ]}
            />
          </motion.div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            PANEL 2 — Custom Software & Aesthetic UX
            Light (#F5F5F7) · Image tile LEFT · Text RIGHT
        ═══════════════════════════════════════════════════════════ */}
        <section className="bg-[#F5F5F7] overflow-hidden">
          <motion.div
            variants={col} initial="hidden"
            whileInView="show" viewport={{ once: true, margin: '-80px' }}
            className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 pt-20 sm:pt-28 lg:pt-32 pb-20 sm:pb-28 lg:pb-32"
          >
            {/* Eyebrow */}
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-12 sm:mb-16">
              <span className="text-[10px] font-mono tracking-[0.2em] text-[#1D1D1F]/20">02</span>
              <div className="h-px w-8 bg-[#1D1D1F]/10" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#6E6E73] font-semibold">Engineering Excellence</span>
            </motion.div>

            {/* Main grid */}
            <div className="grid lg:grid-cols-[45%,1fr] gap-10 lg:gap-16 items-center">

              {/* Image tile — visually left on desktop */}
              <ImageTile
                src={aestheticUxImg}
                alt="Custom software and UX engineering"
                shadow="light"
                className="aspect-[4/5] lg:aspect-auto lg:h-[500px] order-last lg:order-first"
              />

              {/* Text */}
              <div>
                <motion.h2 variants={fadeUp}
                  className="text-[48px] sm:text-[62px] lg:text-[76px] font-extralight text-[#1D1D1F]/30 leading-[1.05] tracking-tight">
                  Custom Software
                </motion.h2>
                <motion.h2 variants={fadeUp}
                  className="text-[48px] sm:text-[62px] lg:text-[76px] font-black text-[#1D1D1F] leading-[1.05] tracking-tight -mt-1">
                  &amp;{' '}
                  <GradientText from="#FF7332" via="#FF1E90" to="#7000FF">
                    Aesthetic UX.
                  </GradientText>
                </motion.h2>

                <motion.p variants={fadeUp}
                  className="mt-8 text-[15px] sm:text-base text-[#6E6E73] leading-[1.75] max-w-[44ch] font-light">
                  We build high-performance web apps, mobile platforms, and secure APIs structured
                  for long-term scalability — with design systems that make every user interaction
                  feel effortless and conversion-optimised.
                </motion.p>

                {/* Feature list — Apple style with minimal icons */}
                <motion.ul variants={fadeUp} className="mt-8 space-y-3">
                  {[
                    { icon: FaCode,     text: 'Modular, scalable architecture — built to last' },
                    { icon: FaDatabase, text: '99.9% uptime SLA across all deployments'        },
                    { icon: FaBolt,     text: 'Enterprise-grade security by design'             },
                  ].map(({ icon: Icon, text }, i) => (
                    <li key={i}
                      className="flex items-start gap-3 text-[14px] text-[#6E6E73] font-light leading-snug">
                      <div className="w-5 h-5 mt-0.5 rounded-md bg-[#1D1D1F]/06 flex items-center justify-center flex-shrink-0">
                        <Icon size={8} className="text-[#1D1D1F]/40" />
                      </div>
                      {text}
                    </li>
                  ))}
                </motion.ul>

                <motion.div variants={fadeUp} className="mt-10">
                  <Link to="/services/software-development"
                    className="inline-flex items-center gap-2.5 text-[15px] font-medium text-[#1D1D1F]/60 hover:text-[#1D1D1F] transition-colors duration-200 group">
                    View engineering program
                    <FaArrowRight size={11}
                      className="text-[#1D1D1F]/30 group-hover:text-[#1D1D1F] group-hover:translate-x-1 transition-all duration-200" />
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            PANEL 3 — Analytics & Conversion Data
            Dark (#1D1D1F) · BIG hero number + Image tile
        ═══════════════════════════════════════════════════════════ */}
        <section className="bg-[#1D1D1F] overflow-hidden">
          <motion.div
            variants={col} initial="hidden"
            whileInView="show" viewport={{ once: true, margin: '-80px' }}
            className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 pt-20 sm:pt-28 lg:pt-32"
          >
            {/* Eyebrow */}
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-12 sm:mb-14">
              <span className="text-[10px] font-mono tracking-[0.2em] text-white/50">03</span>
              <div className="h-px w-8 bg-white/25" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-white/60 font-semibold">Telemetry &amp; Insights</span>
            </motion.div>

            {/* Main grid */}
            <div className="grid lg:grid-cols-[55%,1fr] gap-10 lg:gap-16 items-start">

              {/* Left: big number hero + text */}
              <div>
                {/* Massive gradient number — Apple "A18 chip" energy */}
                <motion.div variants={fadeUp} className="mb-2">
                  <span
                    className="text-[96px] sm:text-[128px] lg:text-[160px] font-black leading-none tracking-tighter tabular-nums"
                    style={{
                      background: 'linear-gradient(120deg,#FF7332 0%,#FF1E90 45%,#7000FF 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    340<span className="text-[60px] sm:text-[80px] lg:text-[100px]">%</span>
                  </span>
                </motion.div>

                <motion.h2 variants={fadeUp}
                  className="text-[28px] sm:text-[36px] font-extralight text-white/60 leading-tight tracking-tight mb-1">
                  Analytics &amp;
                </motion.h2>
                <motion.h2 variants={fadeUp}
                  className="text-[28px] sm:text-[36px] font-bold text-white leading-tight tracking-tight">
                  Conversion Data.
                </motion.h2>

                <motion.p variants={fadeUp}
                  className="mt-6 text-[15px] sm:text-base text-[#86868B] leading-[1.75] max-w-[44ch] font-light">
                  Every budget allocation and creative decision is validated through real-time
                  customer behaviour maps, funnel telemetry, and multi-touch ROAS attribution
                  — not assumptions.
                </motion.p>

                {/* Capability tags */}
                <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mt-7">
                  {['Funnel Analytics', 'Budget Attribution', 'Real-time Dashboards', 'Behavior Tracking'].map((t, i) => (
                    <span key={i}
                      className="px-3 py-1.5 border border-white/[0.2] rounded-lg text-[11px] font-semibold text-white/60">
                      {t}
                    </span>
                  ))}
                </motion.div>

                <motion.div variants={fadeUp} className="mt-8">
                  <Link to="/services"
                    className="inline-flex items-center gap-2.5 text-[15px] font-medium text-white/70 hover:text-white transition-colors duration-200 group">
                    Explore analytics services
                    <FaArrowRight size={11}
                      className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all duration-200" />
                  </Link>
                </motion.div>
              </div>

              {/* Right: image tile */}
              <ImageTile
                src={analyticsDataImg}
                alt="Analytics and conversion data dashboard"
                shadow="dark"
                className="aspect-[3/4] lg:aspect-auto lg:h-[520px]"
              />
            </div>

            {/* Stats bar */}
            <StatsBar
              theme="dark"
              stats={[
                { value: 120,  suffix: '+',  label: 'Brands Scaled'    },
                { value: 340,  suffix: '%',  label: 'Avg. ROI'         },
                { value: 50,   suffix: 'M+', label: 'Monthly Reach'    },
              ]}
            />
          </motion.div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            PANEL 4 — Long-Term Partnerships
            Dark (#070707) — slightly deeper than Panel 1/3
        ═══════════════════════════════════════════════════════════ */}
        <section className="bg-[#070707] overflow-hidden relative">
          {/* Subtle dot grid texture */}
          <svg aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ opacity: 0.04 }}>
            <defs>
              <pattern id="p4g" width="36" height="36" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.75" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#p4g)" />
          </svg>

          {/* Subtle top-left orange glow */}
          <div className="absolute top-0 left-0 w-[600px] h-[350px] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 10% 30%,rgba(255,115,50,.06) 0%,transparent 55%)' }} />

          <motion.div
            variants={col} initial="hidden"
            whileInView="show" viewport={{ once: true, margin: '-60px' }}
            className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 pt-20 sm:pt-28 lg:pt-32 pb-20 sm:pb-28 lg:pb-32"
          >
            {/* Eyebrow */}
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-12 sm:mb-16">
              <span className="text-[10px] font-mono tracking-[0.2em] text-white/50">04</span>
              <div className="h-px w-8 bg-white/25" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-white/60 font-semibold">Commitment</span>
            </motion.div>

            {/* Header row */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 lg:gap-24 mb-14 sm:mb-16">
              <div>
                <motion.h2 variants={fadeUp}
                  className="text-[48px] sm:text-[60px] lg:text-[72px] font-extralight text-white/60 leading-[1.04] tracking-tight">
                  Long-Term
                </motion.h2>
                <motion.h2 variants={fadeUp}
                  className="text-[48px] sm:text-[60px] lg:text-[72px] font-black text-white leading-[1.04] tracking-tight -mt-1">
                  Partnerships.
                </motion.h2>
              </div>

              <motion.div variants={fadeUp} className="max-w-sm space-y-5 lg:pb-2">
                <p className="text-[14px] text-white/70 font-light leading-[1.75]">
                  We operate as an embedded technical partner across your entire growth lifecycle —
                  not a vendor who disappears after delivery.
                </p>
                <div className="flex gap-0 border border-white/[0.08] rounded-xl overflow-hidden divide-x divide-white/[0.08]">
                  {[
                    { n: '30 Day', l: 'Rolling Contracts' },
                    { n: '24 hr',  l: 'Response SLA'      },
                    { n: '120+',   l: 'Active Partners'   },
                  ].map((s, i) => (
                    <div key={i} className="px-5 py-3 text-center flex-1">
                      <div className="text-sm font-bold text-white tabular-nums">{s.n}</div>
                      <div className="text-[10px] text-white/50 uppercase tracking-wider font-semibold mt-0.5">{s.l}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Tier cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
              {[
                {
                  num: '01', title: 'Startups',
                  desc: 'Lean campaigns and tech built to punch above budget. Establish your digital foundation fast.',
                  tags: ['MVP Launch', 'Growth Hacking', 'Brand Identity', 'Performance Ads'],
                  icon: FaRocket,
                },
                {
                  num: '02', title: 'Mid-Size Brands',
                  desc: 'Scale conversion channels, automate pipelines, and compound growth as your senior technical extension.',
                  tags: ['Channel Scaling', 'CRO', 'Automation', 'Campaign Mgmt'],
                  icon: FaChartLine,
                },
                {
                  num: '03', title: 'Enterprises',
                  desc: 'Modernise infrastructure, enforce security frameworks, and run continuous audits at boutique speed.',
                  tags: ['Infrastructure', 'Security Audits', 'Compliance', 'AI Integration'],
                  icon: FaAward,
                },
              ].map((tier, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-30px' }}
                  transition={{ duration: 0.55, ease: E, delay: i * 0.08 }}
                  className="border border-white/[0.07] rounded-[20px] p-7 flex flex-col gap-5
                    hover:border-white/[0.16] transition-colors duration-300 group"
                >
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] font-mono text-white/50 tracking-widest">{tier.num}</span>
                    <div className="w-8 h-8 rounded-lg bg-white/[0.05] border border-white/[0.09] flex items-center justify-center
                      group-hover:bg-white/[0.1] transition-colors duration-300">
                      <tier.icon size={12} className="text-white/50 group-hover:text-white/75 transition-colors duration-300" />
                    </div>
                  </div>
                  <h3 className="text-[18px] font-semibold text-white tracking-tight leading-tight">{tier.title}</h3>
                  <p className="text-[13px] text-white/70 leading-[1.7] font-light flex-1">{tier.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {tier.tags.map((t, j) => (
                      <span key={j}
                        className="px-2.5 py-1 border border-white/[0.2] rounded-full text-[10px] font-semibold text-white/60
                          group-hover:border-white/[0.35] group-hover:text-white/80 transition-colors duration-300">
                        {t}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Bottom CTA */}
            <motion.div
              variants={fadeUp}
              className="mt-10 pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row
                items-start sm:items-center gap-5 justify-between"
            >
              <p className="text-[13px] text-white/50 font-light leading-relaxed max-w-md">
                Dedicated Slack channel · Weekly syncs · Monthly audits · 30-day rolling contracts
              </p>
              <Link to="/contact"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-white text-[#1D1D1F] rounded-full
                  text-[14px] font-semibold hover:bg-[#F5F5F7] transition-colors duration-200 flex-shrink-0 group">
                Start a Partnership
                <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </motion.div>
          </motion.div>
        </section>

      </div>
    );
  }

  // Desktop Scroll Snap Snapping Viewport Grid Rendering
  return (
    <div className="w-full">

      {/* PANEL 1 — Transforming Businesses */}
      <section 
        ref={ref0} 
        className="h-screen w-full bg-[#1D1D1F] flex items-center justify-center snap-start snap-always relative overflow-hidden"
      >
        <div className="max-w-[1200px] w-full mx-auto px-6 sm:px-10 lg:px-16 flex flex-col justify-center">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-10">
            <span className="text-[10px] font-mono tracking-[0.2em] text-white/50">01</span>
            <div className="h-px w-8 bg-white/25" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-white/60 font-semibold">Digital Transformation</span>
          </div>

          {/* Main grid */}
          <div className="grid lg:grid-cols-[55%,1fr] gap-10 lg:gap-16 items-center">
            {/* Text */}
            <motion.div style={{ y: textY0 }}>
              <h2 className="text-[52px] sm:text-[68px] lg:text-[76px] xl:text-[84px] font-extralight text-white/60 leading-[1.04] tracking-tight">
                Transforming
              </h2>
              <h2 className="text-[52px] sm:text-[68px] lg:text-[76px] xl:text-[84px] font-black text-white leading-[1.04] tracking-tight -mt-1 sm:-mt-2">
                Businesses.
              </h2>
              <p className="mt-4 text-lg sm:text-xl font-light leading-none">
                <GradientText from="#FF7332" via="#FF1E90" to="#7000FF">
                  Through Innovation
                </GradientText>
              </p>
              <p className="mt-6 text-[15px] sm:text-base text-[#86868B] leading-[1.75] max-w-[44ch] font-light">
                At Cyborg Sapient, we operate at the intersection of technology and creative
                performance — helping scaling businesses leverage AI automation, custom
                engineering, and data-driven campaigns that compound over time.
              </p>
              <div className="mt-8">
                <Link to="/contact"
                  className="inline-flex items-center gap-2.5 text-[15px] font-medium text-white/70 hover:text-white transition-colors duration-200 group">
                  Start a project
                  <FaArrowRight size={11}
                    className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all duration-200" />
                </Link>
              </div>
            </motion.div>

            {/* Image tile */}
            <motion.div style={{ y: imgY0 }}>
              <ImageTile
                src={digitalInnovationImg}
                alt="Team driving digital transformation"
                shadow="dark"
                className="h-[360px] xl:h-[440px]"
              />
            </motion.div>
          </div>

          {/* Stats bar */}
          <StatsBar
            theme="dark"
            stats={[
              { value: 500, suffix: '+', label: 'Projects Delivered' },
              { value: 98,  suffix: '%', label: 'Client Retention'   },
              { value: 24,  suffix: 'h', label: 'Response SLA'       },
            ]}
          />
        </div>
      </section>

      {/* PANEL 2 — Custom Software & Aesthetic UX */}
      <section 
        ref={ref1} 
        className="h-screen w-full bg-[#F5F5F7] flex items-center justify-center snap-start snap-always relative overflow-hidden"
      >
        <div className="max-w-[1200px] w-full mx-auto px-6 sm:px-10 lg:px-16 flex flex-col justify-center">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-10">
            <span className="text-[10px] font-mono tracking-[0.2em] text-[#1D1D1F]/20">02</span>
            <div className="h-px w-8 bg-[#1D1D1F]/10" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#6E6E73] font-semibold">Engineering Excellence</span>
          </div>

          {/* Main grid */}
          <div className="grid lg:grid-cols-[45%,1fr] gap-10 lg:gap-16 items-center">
            {/* Image tile */}
            <motion.div style={{ y: imgY1 }} className="w-full">
              <ImageTile
                src={aestheticUxImg}
                alt="Custom software and UX engineering"
                shadow="light"
                className="h-[360px] xl:h-[440px]"
              />
            </motion.div>

            {/* Text */}
            <motion.div style={{ y: textY1 }}>
              <h2 className="text-[48px] sm:text-[62px] lg:text-[68px] xl:text-[76px] font-extralight text-[#1D1D1F]/30 leading-[1.05] tracking-tight">
                Custom Software
              </h2>
              <h2 className="text-[48px] sm:text-[62px] lg:text-[68px] xl:text-[76px] font-black text-[#1D1D1F] leading-[1.05] tracking-tight -mt-1">
                &amp;{' '}
                <GradientText from="#FF7332" via="#FF1E90" to="#7000FF">
                  Aesthetic UX.
                </GradientText>
              </h2>
              <p className="mt-6 text-[15px] sm:text-base text-[#6E6E73] leading-[1.75] max-w-[44ch] font-light">
                We build high-performance web apps, mobile platforms, and secure APIs structured
                for long-term scalability — with design systems that make every user interaction
                feel effortless and conversion-optimised.
              </p>

              <ul className="mt-6 space-y-2.5">
                {[
                  { icon: FaCode,     text: 'Modular, scalable architecture — built to last' },
                  { icon: FaDatabase, text: '99.9% uptime SLA across all deployments'        },
                  { icon: FaBolt,     text: 'Enterprise-grade security by design'             },
                ].map(({ icon: Icon, text }, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] text-[#6E6E73] font-light leading-snug">
                    <div className="w-5 h-5 mt-0.5 rounded-md bg-[#1D1D1F]/06 flex items-center justify-center flex-shrink-0">
                      <Icon size={8} className="text-[#1D1D1F]/40" />
                    </div>
                    {text}
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <Link to="/services/software-development"
                  className="inline-flex items-center gap-2.5 text-[15px] font-medium text-[#1D1D1F]/60 hover:text-[#1D1D1F] transition-colors duration-200 group">
                  View engineering program
                  <FaArrowRight size={11}
                    className="text-[#1D1D1F]/30 group-hover:text-[#1D1D1F] group-hover:translate-x-1 transition-all duration-200" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PANEL 3 — Analytics & Conversion Data */}
      <section 
        ref={ref2} 
        className="h-screen w-full bg-[#1D1D1F] flex items-center justify-center snap-start snap-always relative overflow-hidden"
      >
        <div className="max-w-[1200px] w-full mx-auto px-6 sm:px-10 lg:px-16 flex flex-col justify-center">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <span className="text-[10px] font-mono tracking-[0.2em] text-white/50">03</span>
            <div className="h-px w-8 bg-white/25" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-white/60 font-semibold">Telemetry &amp; Insights</span>
          </div>

          {/* Main grid */}
          <div className="grid lg:grid-cols-[55%,1fr] gap-10 lg:gap-16 items-start">
            {/* Left: big number hero + text */}
            <motion.div style={{ y: textY2 }}>
              <div className="mb-1">
                <span
                  className="text-[80px] sm:text-[100px] lg:text-[120px] xl:text-[140px] font-black leading-none tracking-tighter tabular-nums"
                  style={{
                    background: 'linear-gradient(120deg,#FF7332 0%,#FF1E90 45%,#7000FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  340<span className="text-[50px] sm:text-[60px] lg:text-[80px] xl:text-[90px]">%</span>
                </span>
              </div>

              <h2 className="text-[26px] sm:text-[32px] font-extralight text-white/60 leading-tight tracking-tight mb-1">
                Analytics &amp;
              </h2>
              <h2 className="text-[26px] sm:text-[32px] font-bold text-white leading-tight tracking-tight">
                Conversion Data.
              </h2>

              <p className="mt-4 text-[14px] sm:text-[15px] text-[#86868B] leading-[1.7] max-w-[44ch] font-light">
                Every budget allocation and creative decision is validated through real-time
                customer behaviour maps, funnel telemetry, and multi-touch ROAS attribution — not assumptions.
              </p>

              <div className="flex flex-wrap gap-2 mt-5">
                {['Funnel Analytics', 'Budget Attribution', 'Real-time Dashboards', 'Behavior Tracking'].map((t, i) => (
                  <span key={i} className="px-2.5 py-1 border border-white/[0.15] rounded-lg text-[10px] font-semibold text-white/55">
                    {t}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <Link to="/services"
                  className="inline-flex items-center gap-2.5 text-[14px] font-medium text-white/70 hover:text-white transition-colors duration-200 group">
                  Explore analytics services
                  <FaArrowRight size={11}
                    className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all duration-200" />
                </Link>
              </div>
            </motion.div>

            {/* Right: image tile */}
            <motion.div style={{ y: imgY2 }} className="w-full">
              <ImageTile
                src={analyticsDataImg}
                alt="Analytics and conversion data dashboard"
                shadow="dark"
                className="h-[360px] xl:h-[440px]"
              />
            </motion.div>
          </div>

          {/* Stats bar */}
          <StatsBar
            theme="dark"
            stats={[
              { value: 120,  suffix: '+',  label: 'Brands Scaled'    },
              { value: 340,  suffix: '%',  label: 'Avg. ROI'         },
              { value: 50,   suffix: 'M+', label: 'Monthly Reach'    },
            ]}
          />
        </div>
      </section>

      {/* PANEL 4 — Long-Term Partnerships */}
      <section 
        ref={ref3} 
        className="h-screen w-full bg-[#070707] flex items-center justify-center snap-start snap-always relative overflow-hidden"
      >
        {/* Subtle dot grid texture */}
        <svg aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ opacity: 0.04 }}>
          <defs>
            <pattern id="p4g" width="36" height="36" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.75" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#p4g)" />
        </svg>

        {/* Subtle top-left orange glow */}
        <div className="absolute top-0 left-0 w-[600px] h-[350px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 10% 30%,rgba(255,115,50,.06) 0%,transparent 55%)' }} />

        <div className="max-w-[1200px] w-full mx-auto px-6 sm:px-10 lg:px-16 flex flex-col justify-center">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <span className="text-[10px] font-mono tracking-[0.2em] text-white/50">04</span>
            <div className="h-px w-8 bg-white/25" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-white/60 font-semibold">Commitment</span>
          </div>

          {/* Header row */}
          <motion.div 
            style={{ y: textY3 }}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 lg:gap-20 mb-8"
          >
            <div>
              <h2 className="text-[44px] sm:text-[54px] lg:text-[60px] xl:text-[68px] font-extralight text-white/60 leading-[1.04] tracking-tight">
                Long-Term
              </h2>
              <h2 className="text-[44px] sm:text-[54px] lg:text-[60px] xl:text-[68px] font-black text-white leading-[1.04] tracking-tight -mt-1">
                Partnerships.
              </h2>
            </div>

            <div className="max-w-sm space-y-4 lg:pb-1">
              <p className="text-[13px] text-white/70 font-light leading-[1.7]">
                We operate as an embedded technical partner across your entire growth lifecycle — not a vendor who disappears after delivery.
              </p>
              <div className="flex gap-0 border border-white/[0.08] rounded-xl overflow-hidden divide-x divide-white/[0.08]">
                {[
                  { n: '30 Day', l: 'Rolling Contracts' },
                  { n: '24 hr',  l: 'Response SLA'      },
                  { n: '120+',   l: 'Active Partners'   },
                ].map((s, i) => (
                  <div key={i} className="px-4 py-2.5 text-center flex-1">
                    <div className="text-xs font-bold text-white tabular-nums">{s.n}</div>
                    <div className="text-[9px] text-white/50 uppercase tracking-wider font-semibold mt-0.5">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Tier cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                num: '01', title: 'Startups',
                desc: 'Lean campaigns and tech built to punch above budget. Establish your digital foundation fast.',
                tags: ['MVP Launch', 'Growth Hacking', 'Brand Identity', 'Performance Ads'],
                icon: FaRocket,
              },
              {
                num: '02', title: 'Mid-Size Brands',
                desc: 'Scale conversion channels, automate pipelines, and compound growth as your senior technical extension.',
                tags: ['Channel Scaling', 'CRO', 'Automation', 'Campaign Mgmt'],
                icon: FaChartLine,
              },
              {
                num: '03', title: 'Enterprises',
                desc: 'Modernise infrastructure, enforce security frameworks, and run continuous audits at boutique speed.',
                tags: ['Infrastructure', 'Security Audits', 'Compliance', 'AI Integration'],
                icon: FaAward,
              },
            ].map((tier, i) => (
              <motion.div key={i}
                className="border border-white/[0.07] rounded-[20px] p-6 flex flex-col gap-4
                  hover:border-white/[0.16] transition-colors duration-300 group"
              >
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-mono text-white/50 tracking-widest">{tier.num}</span>
                  <div className="w-8 h-8 rounded-lg bg-white/[0.05] border border-white/[0.09] flex items-center justify-center
                    group-hover:bg-white/[0.1] transition-colors duration-300">
                    <tier.icon size={11} className="text-white/50 group-hover:text-white/75 transition-colors duration-300" />
                  </div>
                </div>
                <h3 className="text-[16px] font-semibold text-white tracking-tight leading-tight">{tier.title}</h3>
                <p className="text-[12px] text-white/70 leading-[1.6] font-light flex-1">{tier.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {tier.tags.map((t, j) => (
                    <span key={j}
                      className="px-2 py-1 border border-white/[0.15] rounded-full text-[9px] font-semibold text-white/55
                        group-hover:border-white/[0.3] group-hover:text-white/75 transition-colors duration-300">
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div
            className="mt-8 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row
              items-start sm:items-center gap-5 justify-between"
          >
            <p className="text-[12px] text-white/50 font-light leading-relaxed max-w-md">
              Dedicated Slack channel · Weekly syncs · Monthly audits · 30-day rolling contracts
            </p>
            <Link to="/contact"
              className="inline-flex items-center gap-2.5 px-6 py-3 bg-white text-[#1D1D1F] rounded-full
                text-[13px] font-semibold hover:bg-[#F5F5F7] transition-colors duration-200 flex-shrink-0 group">
              Start a Partnership
              <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default EditorialSection;
